import React from 'react';
export interface IndustryWork {
  id: number;
  title: string;
  subtitle?: string;
  company: string;
  imageUrl: string;
  tags: string[];
  problemContext: string;
  researchInsights: {
    description: string;
    sources?: string[];
    keyInsights: string[];
  };
  productGoal: {
    description: string;
    objectives: string[];
  };
  solutionApproach: {
    description: string;
    role: string;
  };
  keyContributions: {
    title: string;
    points: string[];
  }[];
  impact: string[];
  productThinkingSnapshot: {
    title: string;
    description: string;
  }[];
  tools: {
    name: string;
    icon: string;
  }[];
  reflection: string;
}
export const industryWorks: IndustryWork[] = [{
  id: 1,
  title: 'Reimagining Subscription Journeys for a Modern Commerce Platform',
  subtitle: 'A multi-market initiative to rebuild subscription journeys, unify core capabilities, and deliver a scalable commerce experience.',
  company: 'Amway',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iomPfmn47NsZYPfRT31d3P/amway.png',
  tags: ['Subscription', 'E-commerce', 'UX Modernization', 'Multi-market'],
  problemContext: "Amway's subscription business is a key contributor to customer repeat usage and long-term value. However, the existing subscription experience had several usability gaps that led to friction in discovery, enrollment, and management. As part of a broader modernization initiative, we aimed to improve the subscription experience and make it more intuitive for both distributors (ABOs) and customers.",
  researchInsights: {
    description: 'I conducted discovery across:',
    sources: ['Existing subscription journeys', 'Market-specific behaviors', 'User pain points in enrollment and payments', 'Competitive journeys in health & wellness subscriptions'],
    keyInsights: ['Users struggled to understand plan benefits and renewal terms', 'Multiple decision points created drop-offs in the funnel', 'Market variations required a flexible, configurable system', 'The storefront did not clearly present subscription value upfront']
  },
  productGoal: {
    description: 'Create a simplified, modern subscription flow that:',
    objectives: ['Reduces friction across enrollment and management', 'Helps users understand value propositions clearly', 'Supports market flexibility without custom builds', 'Lays the foundation for scaled rollout across regions']
  },
  solutionApproach: {
    description: 'I worked as both a Product Owner and Business Analyst, contributing to the full product lifecycle:',
    role: 'Product Owner & Business Analyst'
  },
  keyContributions: [{
    title: 'User & Market Understanding',
    points: ['Analyzed how different user groups (customers, guests, ABOs) interacted with subscription journeys', 'Studied market nuances to ensure the solution worked globally']
  }, {
    title: 'Experience Redesign',
    points: ['Proposed clearer layouts for subscription discovery and plan explanation', 'Simplified steps across enrollment and change-subscription journeys']
  }, {
    title: 'Product Requirements & Delivery',
    points: ['Authored detailed requirements for subscription capabilities (e.g., communications, payments, rules, benefits)', 'Drove discussions with engineering, UX, and supporting teams to ensure alignment', 'Prioritized backlog items for phased delivery']
  }, {
    title: 'Feature Ownership',
    points: ['Owned core subscription capabilities end-to-end, including plan setup, payment flows, communication logic, and user management actions']
  }],
  impact: ['Reduced friction across the user journey', 'Improved clarity around plan benefits and actions', 'Supported stronger adoption of subscription offerings'],
  productThinkingSnapshot: [{
    title: 'User Value',
    description: 'Easier discovery, clearer benefits, smoother renewals'
  }, {
    title: 'Business Value',
    description: 'Stronger adoption and retention of subscription offerings'
  }, {
    title: 'Feasibility',
    description: 'Delivered in a modular manner to support multiple markets'
  }, {
    title: 'Risks',
    description: 'Ensuring market flexibility without complicating core flows'
  }, {
    title: 'Future Scope',
    description: 'Personalization, improved plan recommendations, and predictive renewal prompts'
  }],
  tools: [{
    name: 'JIRA',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jira/jira-original.svg'
  }, {
    name: 'Confluence',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/confluence/confluence-original.svg'
  }, {
    name: 'Figma',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg'
  }],
  reflection: 'This project strengthened my foundation in building scalable, multi-market digital experiences. It taught me how to convert complex business needs into simple user flows, manage cross-functional dependencies, and balance global consistency with local variability. It remains one of my most meaningful contributions as a Product Owner.'
}, {
  id: 2,
  title: 'Global Sales & Market Insights Platform',
  subtitle: 'Standardizing Reporting & Decision Intelligence Across Regions',
  company: 'Deloitte',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sdC3p6y1DHNaNVTZ5FHPE5/deloitte.png',
  tags: ['Data Analytics', 'Enterprise Reporting', 'KPI Standardization', 'Global Platform'],
  problemContext: 'A global consumer goods organization managed massive volumes of sales, market, and retailer data across multiple regions. However, reporting practices were highly inconsistent — teams relied on manual processes, disconnected excel sheets, and region-specific dashboards that made it difficult to compare performance or make timely decisions.\n\nThe company needed a unified reporting platform that could standardize KPIs, streamline data flows, and provide a single, credible view of performance across categories, customers, and markets.',
  researchInsights: {
    description: 'Through stakeholder discussions across sales, marketing, finance, and planning teams, I identified key challenges:',
    keyInsights: ['Reporting logic varied significantly across regions', 'Teams lacked standardized templates and KPI definitions', 'Too much time was spent manually assembling data from different systems', 'No central dashboard existed to track trends, forecasts, or retailer performance', 'Business users needed dynamic filtering and customizable views']
  },
  productGoal: {
    description: 'Build a scalable reporting product that:',
    objectives: ['Standardizes performance metrics across all major sales and planning functions', 'Enables teams to visualize insights dynamically and securely', 'Reduces operational inefficiencies caused by fragmented reporting', 'Supports multiple regions with minimal customization', 'Provides a consistent, user-friendly reporting experience for decision-makers']
  },
  solutionApproach: {
    description: 'As the Business Analyst on the product, I collaborated across business, data, and design teams to define and deliver the solution.',
    role: 'Business Analyst'
  },
  keyContributions: [{
    title: 'Understanding Business Needs',
    points: ['Studied existing sales and planning workflows across regions', 'Gathered high-level requirements through stakeholder interviews', 'Identified essential KPIs and reporting needs for various teams']
  }, {
    title: 'Detailed Requirements & KPI Definitions',
    points: ['Broke high-level needs into granular reporting requirements', 'Worked closely with business users to clarify data structures, refresh cycles, and calculation rules', 'Defined filter behavior, data fields, and visualization needs for each report']
  }, {
    title: 'Design & Data Collaboration',
    points: ['Worked with the design team to create a consistent dashboard template', 'Aligned with data engineering teams to validate data availability and mapping', 'Ensured reporting logic matched business expectations before sprint development']
  }, {
    title: 'Agile Delivery',
    points: ['Participated in sprint planning, backlog refinement, and prioritization', 'Managed cross-team dependencies and resolved clarification needs', 'Collaborated with the Product Owner to track progress and remove blockers']
  }],
  impact: ['Faster, more accurate insight generation across sales and planning teams', 'Reduced dependency on manual excel-based reporting', 'Standardized definitions for KPIs and metrics across all regions', 'Secure, role-based data access for different user groups', 'Better decision-making through centralized and consistent reporting'],
  productThinkingSnapshot: [{
    title: 'User Value',
    description: 'One place to track sales trends, retailer performance, and forecasts; Consistent report templates that reduce learning time; Intuitive filtering and drill-down options tailored to each role'
  }, {
    title: 'Business Value',
    description: 'Improved decision quality through reliable, standardized insights; Reduced manual effort and reporting inefficiencies; Scalable platform ready for additional regions and functions'
  }, {
    title: 'Feasibility',
    description: 'Built using existing cloud infrastructure and reusable data pipelines; Standard template allowed easy onboarding of new markets'
  }, {
    title: 'Risks',
    description: 'Variations in data quality and availability across regions; Complex KPI logic requiring continuous stakeholder validation'
  }, {
    title: 'Future Scope',
    description: 'Predictive analytics for sales and demand planning; Region-specific personalization; Expansion into promotional and category analytics'
  }],
  tools: [{
    name: 'Excel',
    icon: 'https://img.icons8.com/color/96/microsoft-excel-2019--v1.png'
  }, {
    name: 'Power BI',
    icon: 'https://img.icons8.com/color/96/power-bi.png'
  }, {
    name: 'JIRA',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jira/jira-original.svg'
  }, {
    name: 'Azure Data Lake',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg'
  }, {
    name: 'Confluence',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/confluence/confluence-original.svg'
  }],
  reflection: 'This project strengthened my expertise in translating complex business processes into scalable, data-driven products. I gained deep experience in KPI standardization, stakeholder alignment, data interpretation, and building global reporting capabilities that support strategic decisions. It also refined my ability to collaborate across data, design, and engineering teams in an agile setup.'
}, {
  id: 3,
  title: 'Network Deployment Automation Platform',
  subtitle: 'Accelerating 4G/5G Rollouts through Workflow Automation',
  company: 'Ericsson',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3ihuGTsyWbtQedG3TJzY2p/ericsson.png',
  tags: ['Telecom', 'Workflow Automation', '4G/5G Deployment', 'Operations'],
  problemContext: 'Telecom network deployments require multiple teams—field engineers, network specialists, and operational coordinators—to work in tight maintenance windows. Earlier, many of these interactions were handled manually: field engineers shared details over calls, network teams ran pre-checks and post-checks manually, and updates were shared via emails or spreadsheets.\n\nThese fragmented workflows created delays, increased dependency on individuals, and slowed down overall site readiness. To improve operational efficiency, there was a need for an automation platform that could streamline routine steps, reduce friction between teams, and help accelerate deployment cycles.',
  researchInsights: {
    description: 'Through stakeholder discussions, interviews, and process mapping, a few key pain points emerged:',
    keyInsights: ['Routine tasks like pre-checks, report preparation, and log handling were heavily manual', 'Field engineers spent long waiting periods before starting site activities', 'Repetitive data entry increased the chance of human error', 'Ticket creation and escalation workflows lacked speed and consistency', 'There was limited real-time visibility into progress across regions and teams']
  },
  productGoal: {
    description: 'Design an automation-driven platform that:',
    objectives: ['Simplifies and accelerates deployment workflows', 'Reduces wait time for field engineers', 'Ensures accuracy in checks and data flow', 'Provides real-time visibility to operational teams', 'Standardizes processes across regions', 'Enhances overall readiness for 4G/5G site implementation']
  },
  solutionApproach: {
    description: 'I worked as a Business Analyst, collaborating across engineering, operations, and field teams to design and deliver the automation platform.',
    role: 'Business Analyst'
  },
  keyContributions: [{
    title: 'Discovery & Understanding the Workflow',
    points: ['Partnered with engineering, network operations, and field teams to understand current challenges', 'Mapped existing processes to identify bottlenecks and potential areas for automation', 'Prioritized features based on operational impact']
  }, {
    title: 'Requirements & Functional Design',
    points: ['Created BRDs, FRDs, user journeys, process flows, and use cases', 'Collaborated with the UX team to design clear and intuitive interfaces', 'Defined functional logic for system checks, ticketing flows, log handling, and escalation paths', 'Worked closely with developers to translate business needs into technical specifications']
  }, {
    title: 'Cross-Functional Collaboration',
    points: ['Coordinated with UI/UX, development, testing, and client teams', 'Led requirement walkthroughs, clarifications, and design alignment meetings', 'Supported QA teams with functional and UI validation', 'Conducted training sessions and prepared documentation for end users']
  }, {
    title: 'Incremental Delivery',
    points: ['Across multiple sprints, the tool evolved from basic automation to a more intelligent workflow system', 'Automated pre-check and post-check reporting', 'Automated ticket creation and closure', 'Automated lock/unlock operations', 'Uploading and downloading of operational logs', 'Escalation workflows for critical scenarios', 'Real-time dashboards for deployment visibility']
  }],
  impact: ['Faster initiation of site activities', 'Consistent and accurate execution of routine tasks', 'Reduced dependency on manual coordination', 'Better collaboration between field engineers and network teams', 'Real-time visibility into ongoing activities'],
  productThinkingSnapshot: [{
    title: 'User Value',
    description: 'A seamless workflow that minimizes waiting time; Clear and guided processes, reducing manual effort; Faster issue resolution through automated escalations'
  }, {
    title: 'Business Value',
    description: 'Streamlined operations for large-scale deployments; More predictable and standardized processes; Improved oversight through centralized dashboards'
  }, {
    title: 'Feasibility',
    description: 'Leveraged automation frameworks already in use; Integrated with existing systems to support operational workflows'
  }, {
    title: 'Risks',
    description: 'Automation reliability dependent on underlying systems; Required effective change management due to shift from manual processes'
  }, {
    title: 'Future Opportunities',
    description: 'Predictive insights to identify issues proactively; Further automation of complex workflows; Scaling the platform across additional regions and deployment projects'
  }],
  tools: [{
    name: 'JIRA',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jira/jira-original.svg'
  }, {
    name: 'Power BI',
    icon: 'https://img.icons8.com/color/96/power-bi.png'
  }, {
    name: 'MS Word',
    icon: 'https://img.icons8.com/color/96/microsoft-word-2019--v2.png'
  }, {
    name: 'Excel',
    icon: 'https://img.icons8.com/color/96/microsoft-excel-2019--v1.png'
  }, {
    name: 'PowerPoint',
    icon: 'https://img.icons8.com/color/96/microsoft-powerpoint-2019--v1.png'
  }, {
    name: 'Lucidchart',
    icon: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1xDDooN1TXet2bR9PXxXqJ/download.png'
  }, {
    name: 'Balsamiq',
    icon: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gaAz74QGdhp2vHMn2VFTxJ/logo.svg'
  }],
  reflection: 'This project strengthened my understanding of telecom deployment workflows, automation feasibility, and large-scale operations. I learned how to balance user needs with technical constraints, manage diverse stakeholders, and translate complex engineering activities into clear product requirements.\n\nIt also enhanced my confidence in designing automation-led solutions that improve user experience and operational efficiency.'
}];